import { Form, Formik, FormikProps } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import useIndexDBValue from '../../../../hook/useIndexDBValue';
import { AppContext } from '../../../appContext/AppContext';
import { KEY_LOCALSTORAGE } from '../../../auth/core/_consts';
import FormInPhieuDialog from '../../../component/button-in-phieu/components/PrintDialog';
import { ConfirmDialog } from '../../../component/ConfirmDialog';
import { IconButtonSave } from '../../../component/IconSvg';
import { IDanhSachDanhMuc } from '../../../phan-he-tiep-nhan-thanh-toan/models/TiepDonModel';
import {
	APP_TIMESTAMP_FORMAT,
	CODE, KEY_DS_DANH_MUC_TIEP_DON, MESSAGE, ORDER_TYPE_ID,
	REQUEST_FROM,
	REQUEST_TYPE,
	RESPONSE_MESSAGE
} from '../../../utils/Constant';
import { formatDateTime } from '../../../utils/FormatUtils';
import { localStorageItem } from '../../../utils/LocalStorage';
import {
	WAREHOUSE,
	WAREHOUSE_TYPE
} from '../../constants/Thuoc';
import { WAREHOUSE_SLIP_TYPE } from '../../constants/VatTu';
import {
	IInPhieuChiDinhThuocParams,
	IKeThuocRequest,
	IKeThuocThongTinChung
} from '../../models/ThuocModels';
import { getThongTinChanDoan } from '../../services/KhamBenhService';
import {
	getDetailDonThuoc,
	getDrugDetailById,
	printPhieuChiDinhThuoc,
	saveDonThuoc,
	saveDonThuocDVKT,
	validateDonThuoc
} from '../../services/ThuocService';
import { getCellEditValue } from '../modal-ke-vat-tu/common';
import OrderAmount from './components/chi-phi-don-thuoc';
import OrderHeader from './components/header';
import IndicationTable from './components/table-phieu-chi-dinh';
import OrderInfo from './components/thong-tin-don-thuoc/ThongTinDon';
import DrugInfo from './components/thong-tin-don-thuoc/ThongTinThuoc';
import { getBirthdayDrugParam, getPriceByObjectType } from './utils';

type Props = {
	open: boolean;
	handleClose: () => void;
	itemEdit?: any;
	onSave?: () => void;
	isFunctionalFoods?: boolean;
	generalInfo?: any;
};

const PageKeThuoc = ({
	open,
	handleClose,
	itemEdit,
	onSave,
	isFunctionalFoods,
	generalInfo,
}: Props) => {
	const { setIsLoading } = useContext(AppContext);
	const { value: categories } = useIndexDBValue<IDanhSachDanhMuc>({
		key: KEY_DS_DANH_MUC_TIEP_DON,
	});

	const formikRef = useRef<FormikProps<any>>(null);
	const formRef = useRef<HTMLFormElement>(null);
	const rowSelectedRef = useRef<any>(null);
	const drugIdRef = useRef<string>('');
	const deletedListRef = useRef<Map<string, any>>(new Map());
	const isPrintRef = useRef(false);

	const [tableData, setTableData] = useState<any[]>([]);
	const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false);
	const [validateMessage, setValidateMessage] = useState<string>('');
	const [shouldOpenConfirmDialog, setShouldOpenConfirmDialog] = useState<boolean>(false);
	const [isEdit, setIsEdit] = useState<boolean>(false);

	const {
		patient,
		visit,
		encounterDiagnosis,
		encounter,
		isConclusion,
		orderId,
		fulfillDeptName,
		forDVKT,
		isReturn,
	} = generalInfo ?? {};
	const userInfo = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);
	const userMetaData = localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA);
	const DRUG_ORDER_TYPE = parseInt(ORDER_TYPE_ID.THUOC);

	const initialValues = {
		drugInfo: {
			totalQuantity: '',
		},
		orderInfo: {
			treatmentDoctorUser: userInfo.sub,
			treatmentDoctorName: userInfo.name,
			doctorOrderDay: formatDateTime(new Date(), APP_TIMESTAMP_FORMAT),
			provisionalDiagnosis: encounterDiagnosis?.provisionalDiagnosis,
			provisionalDiagnosisName: encounterDiagnosis?.provisionalDiagnosisName,
			comorbidity: encounterDiagnosis?.comorbidity,
		},
	};
	
	const validationSchema = Yup.object().shape({
		drugInfo: Yup.object().shape({
			dosageForm: Yup.string().nullable().trim().required(MESSAGE.REQUIRED),
			totalQuantity: Yup.number()
				.required(MESSAGE.REQUIRED)
				.min(1, MESSAGE.REQUIRED)
				.when('iventory', (iventory, schema) => {
					return schema.max(
						iventory ?? 0,
						'Số lượng thuốc kê đơn không lớn hơn số lượng thuốc khả dụng'
					);
				}),
		}),
	});

	useEffect(() => {
		if (itemEdit?.id) {
			getOrderDetail(itemEdit.id);
			setIsEdit(true);
			return;
		}

		getDiagnosis();
	}, [itemEdit]);

	const getDiagnosis = async () => {
		try {
			const { data } = await getThongTinChanDoan(visit?.id);
			if (data?.code === CODE.SUCCESS) {
				formikRef.current &&
				formikRef.current?.setFieldValue('orderInfo', {
					...initialValues.orderInfo,
					provisionalDiagnosis:
						data?.data?.[isConclusion ? 'icdXPrimary' : 'provisionalDiagnosis'] ||
						'',
					provisionalDiagnosisName:
						data?.data?.[
							isConclusion ? 'icdXPrimaryName' : 'provisionalDiagnosisName'
						] || '',
					comorbidity: data?.data?.comorbidity || '',
					diagnosisNonCoded: data?.data?.diagnosisNonCoded || '',
				});
			}
		} catch (error) {
			console.error(error);
		}
	};

	const onSaveForm = async () => {
		try {
			if (!tableData.length) {
				toast.warn('Hiện tại chưa có thuốc được kê cho đơn này');
				return;
			}

			const orderInfo = getFormikValues('orderInfo');
			if (!orderInfo?.provisionalDiagnosisName && !orderInfo?.diagnosisNonCoded) {
				toast.warn('Chưa nhập thông tin chẩn đoán!');
				return;
			}

			setIsLoading(true);

			const warehouseSlipType =
				orderInfo?.warehouse?.warehouseType === WAREHOUSE_TYPE.INSURANCE
					? WAREHOUSE_SLIP_TYPE.INS_AND_INPATIENT
					: WAREHOUSE_SLIP_TYPE.DRUG;
			const drugList: IKeThuocRequest[] = [];

			tableData.forEach((row: any) => {
				drugList.push({
					...row,
					id: row.rowId ?? null,
					patientId: patient.id,
					encounterId: encounter?.id,
					visitId: visit?.id,
					orderId: row.orderId,
					unitPrice: row.donGia,
					intoMoney: row.thanhTien,
					orderTypeId: DRUG_ORDER_TYPE,
					svcFee: row.servicePrice,
					svcFeeIns: row.bhytPrice,
					svcFeeHospital: row.hospitalPrice,
				});
			});

			const donThuocRequest: IKeThuocThongTinChung = {
				...orderInfo,
				patientId: patient.id,
				visitId: visit?.id,
				encounterId: encounter?.id,
				orderId,
				orderTypeId: DRUG_ORDER_TYPE,
				warehouseName: orderInfo?.warehouse?.name,
				warehouseId: orderInfo?.warehouse?.id,
				warehouseType: orderInfo?.warehouse?.warehouseType,
				reExaminationDate: orderInfo?.ngayTaiKham
					? new Date(orderInfo.ngayTaiKham).toISOString()
					: null,
				designatedDate: formatDateTime(new Date(), APP_TIMESTAMP_FORMAT),
				designatedPerson: userInfo.name,
				designatedPlace: fulfillDeptName,
				lstMedicalItemOrder: drugList.concat(Array.from(deletedListRef.current.values())),
				gender: patient.gender,
				birthDay: getBirthdayDrugParam(patient),
				warehouseSlipType,
				...(generalInfo?.forDVKT && {
					orderGroupId: generalInfo?.orderGroupId,
					type: DRUG_ORDER_TYPE,
				}),
				formCode: !forDVKT ? REQUEST_FROM.KHAM_BENH : isReturn ? REQUEST_FROM.HOAN_TRA : REQUEST_FROM.HAO_PHI,
				medicalItemTypeCode: REQUEST_TYPE.THUOC,
				visitObjectTypeCode: visit?.visitObjectTypeCode,
				pid: userMetaData?.personId,
			};

			if (!validateMessage) {
				const validateResponse = await validateDonThuoc(donThuocRequest);
				if (validateResponse?.code === CODE.SUCCESS) {
					setValidateMessage('');
				} else {
					const errorMessage =
						validateResponse?.data?.[0]?.errorMessage || RESPONSE_MESSAGE.ERROR;
					setValidateMessage(errorMessage);
					setShouldOpenConfirmDialog(true);
					return;
				}
			}

			await savePrescription(donThuocRequest);
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const savePrescription = async (params: any) => {
		try {
			const { code, data } = generalInfo?.forDVKT
				? await saveDonThuocDVKT(params, savePrescription, orderId, generalInfo?.isReturn)
				: await saveDonThuoc(params, savePrescription);

			if (CODE.SUCCESS !== code) {
				toast.error(data?.[0]?.errorMessage || RESPONSE_MESSAGE.ERROR);
				return;
			}

			toast.success(
				drugIdRef.current || generalInfo?.forDVKT
					? RESPONSE_MESSAGE.UPDATE.SUCCESS
					: RESPONSE_MESSAGE.ADD.SUCCESS
			);
			drugIdRef.current = data;
			if (isPrintRef.current) {
				setOpenPrintDialog(true);
				return;
			}
			onSave?.();
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	const getOrderDetail = async (thuocId: string) => {
		try {
			setIsLoading(true);

			const { code, data } = await getDetailDonThuoc(
				thuocId,
				!forDVKT ? orderId : null
			);

			if (code === CODE.SUCCESS) {
				drugIdRef.current = data.id;
				const { lstMedicalItemOrderRes, ...orderData } = data;

				formikRef.current?.setValues({
					orderInfo: {
						...orderData,
						doctorOrderDay:
							data.doctorOrderDay ?? formatDateTime(new Date(), APP_TIMESTAMP_FORMAT),
						warehouse: {
							id: data.warehouseId,
							name: data.warehouseName,
							warehouseType: data.warehouseType,
						},
					},
				});

				if ((lstMedicalItemOrderRes || []).length) {
					setTableData(mapDrugListResponseToTable(lstMedicalItemOrderRes));
				}
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const mapDrugListResponseToTable = (data: any[]) => {
		return data.map((row: any) => ({
			...row,
			rowId: row.id,
			tenThuoc: row.name,
			hoatChat: row.activeIngredients,
			donVi: row.unit,
			soLuong: row.totalQuantity,
			duongDung: row.route,
			lieuDung: row.dosageForm,
			donGia: row.unitPrice,
			thanhTien: row.intoMoney,
			doiTuong: row.visitObjectTypeName,
			huongDanSuDungThuoc: row.usageInstructions,
			donViTinh: row.unit,
			tongSoThuoc: row.totalQuantity,
			soNgayDung: row.numberOfDays,
			maThuoc: row.medicalItemCode,
			servicePrice: row.svcFee,
			hospitalPrice: row.svcFeeHospital,
			bhytPrice: row.svcFeeIns,
		}));
	};

	const getDrugDetail = async (drugId: number | string, params: any) => {
		try {
			setIsLoading(true);
			const response = await getDrugDetailById(drugId, {
				warehouseId: params.warehouseId,
				consignment: params.consignment,
				serialNumber: params.serialNumber,
			});

			return response?.data;
		} catch (error) {
			console.error(error);
			toast.error('Đã có lỗi xảy ra khi lấy thông tin thuốc');
		} finally {
			setIsLoading(false);
		}
	};

	const updateRowData = (rowIndex: number, field: string, value: any) => {
		const tableDataTemp = [...tableData];
		const rowUpdate = tableDataTemp.find((row: any, index: number) => index === rowIndex);
		const editedValue = getCellEditValue({
			rowUpdate,
			field,
			value,
			categories,
			patientInfo: generalInfo,
		});
		const updatedList = tableDataTemp.map((row: any, index) => {
			if (index === rowIndex) {
				return {
				...row,
				...editedValue,
				};
			}
			return row;
		});
		setTableData(updatedList);
  	};

	const onSaveDrug = () => {
		const drugData = getFormikValues('drugInfo');
		const orderData = getFormikValues('orderInfo');

		if (!drugData.name) {
			toast.warning('Chưa chọn thuốc kê đơn');
			return;
		}

		const isKhoNhaThuoc = WAREHOUSE.PHARMACY.ID === orderData?.warehouse?.id;
		const unitPrice = isKhoNhaThuoc
			? drugData?.salePrice ?? drugData?.donGia ?? 0
			: getPriceByObjectType(
					drugData,
					drugData?.objectTypeCode ?? visit?.visitObjectTypeCode
			  );
		const rowData = {
			...drugData,
			unitPrice,
			intoMoney: drugData.totalQuantity * unitPrice,
			percentIns: drugData.rateHealthInsurance,
			routeCode: drugData.ROA,
			visitObjectTypeCode: visit?.visitObjectTypeCode,
			visitObjectTypeName: visit?.visitObjectTypeName,
			objectTypeCode: drugData?.objectTypeCode ?? visit?.visitObjectTypeCode,
			objectTypeName: drugData?.objectTypeName ?? visit?.visitObjectTypeName,

			tenThuoc: drugData.name,
			maThuoc: drugData.medicalItemCode,
			hoatChat: drugData.actIngName,
			donVi: drugData.unit,
			soLuong: drugData.totalQuantity,
			duongDung: drugData.route,
			lieuDung: drugData.dosageForm,
			huongDanSuDungThuoc: drugData.usageInstructions,
			donGia: unitPrice,
			thanhTien: drugData.totalQuantity * unitPrice,
		};

		let tableDataTemp = [...tableData];
		const drugExist = tableData.some((row: any) => (row.maThuoc === drugData.medicalItemCode && row?.consignment === drugData?.consignment));

		if (!drugExist) {
			tableDataTemp.push(rowData);
		} else {
			tableDataTemp = tableData.map((row: any) => {
				if (row.maThuoc === rowData.maThuoc && row?.consignment === drugData?.consignment) {
					return {
						...row,
						...rowData,
					};
				}
				return row;
			});
		}

		setTableData(tableDataTemp);
		onClearDrug();
	};

	const onClearDrug = () => {
		formikRef.current?.setFieldValue('drugInfo', {});
		formikRef.current?.setFieldError('drugInfo', undefined);
		formikRef.current?.setFieldTouched('drugInfo', false);
	};

	const onEditDrug = async (rowData: any) => {
		const formValues = formikRef.current?.values;
		const warehouseId = formValues.orderInfo?.warehouse?.id;

		const drugSelected = await getDrugDetail(rowData.medicalItemId, {
			warehouseId,
			consignment: rowData.consignment,
			serialNumber: rowData.serialNumber,
		});

		if (!drugSelected) return;

		formikRef.current?.setFieldValue('drugInfo', {
			...rowData,
			iventory: drugSelected.availableQuantity,
		});
	};

	const onDeleteDrug = () => {
		const rowDelete = rowSelectedRef.current;

		if (rowDelete?.rowId) {
			deletedListRef.current.set(rowDelete.rowId, {
				...rowDelete,
				deleted: true,
			});
		}

		const listTemp = tableData.filter(
			(row: any) => row.medicalItemCode !== rowDelete.medicalItemCode || row?.consignment !== rowDelete?.consignment
		);
		setTableData(listTemp);
	};

	const getFormikValues = (field?: string) => {
		return field ? formikRef.current?.values[field] : formikRef.current?.values;
	};

	const onDrugSelect = (selectedDrug: any) => {
		if (!selectedDrug && getFormikValues('drugInfo')) {
			formikRef.current?.setFieldValue('drugInfo', null);
			return;
		}

		const isExistSelectedDrug = tableData.some((row: any) => 
			row.maThuoc === selectedDrug?.code && row?.consignment === selectedDrug?.consignment
		);

		if (isExistSelectedDrug) {
			toast.warn(`Thuốc "${selectedDrug.name}" đã được kê trong đơn này`);
			return;
		}

		formikRef.current?.setFieldValue('drugInfo', {
			...selectedDrug,
			id: null,
			inventory: selectedDrug?.iventory,
			unit: selectedDrug?.unitofMeasureName,
			route: selectedDrug?.ROAName,
			medicalItemId: selectedDrug?.id,
			medicalItemCode: selectedDrug?.code,
			medicalItemName: selectedDrug?.name,
			dosageForm: selectedDrug?.dosageStrength,
		});
	}

	return (
		<Modal
			className="page-full"
			fullscreen
			dialogClassName="h-modal"
			show={open}
			backdropClassName="spaces top-50"
		>
			<Formik<any>
				validationSchema={validationSchema}
				onSubmit={onSaveDrug}
				innerRef={formikRef}
				initialValues={initialValues}
			>
				<>
					<div className="thuoc-dialog h-100">
						<Modal.Header className="py-3">
							<OrderHeader 
								generalInfo={generalInfo}
								onClose={handleClose}
								tableData={tableData}
								setTableData={setTableData}
							/>
						</Modal.Header>

						<Modal.Body className="py-6 px-0 spaces overflow-hidden spaces h-calc-vh-154">
							<Form ref={formRef}>
								<OrderInfo 
									generalInfo={generalInfo} 
									disabledWarehouse={!!tableData.length}
									isEdit={isEdit}
								/>
								<DrugInfo
									isFunctionalFoods={isFunctionalFoods}
									onClear={onClearDrug}
									onDrugSelect={onDrugSelect}
								/>
							</Form>
							<IndicationTable 
								tableData={tableData}
								categories={categories}
								rowSelectedRef={rowSelectedRef}
								onEditDrug={onEditDrug}
								updateRowData={updateRowData}
								onDeleteDrug={onDeleteDrug}
							/>							
						</Modal.Body>

						<Modal.Footer className="d-flex justify-content-between position-relative spaces bottom-10 px-10 py-4">
							<OrderAmount
								tableData={tableData}
								generalInfo={generalInfo}
							/>
							<div className="d-flex gap-3">
								<Button
									className="btn-outline min-w-100px"
									onClick={() => {
										isPrintRef.current = true;
										onSaveForm();
									}}
								>
									<i className="bi bi-printer" />
									Lưu và in
								</Button>
								<Button
									className="btn-fill"
									onClick={() => {
										onSaveForm();
									}}
								>
									<IconButtonSave /> Lưu lại
								</Button>
							</div>
						</Modal.Footer>
					</div>

					{openPrintDialog && (
						<FormInPhieuDialog<IInPhieuChiDinhThuocParams>
							show={openPrintDialog}
							onHide={() => {
								isPrintRef.current = false;
								setOpenPrintDialog(false);
								onSave?.();
							}}
							fetchExport={printPhieuChiDinhThuoc}
							params={{
								fileType: 'pdf',
								patientId: patient?.id,
								medicalItemGroupId: drugIdRef.current,
								visitId: visit?.id,
								type: DRUG_ORDER_TYPE,
							}}
						/>
					)}

					{shouldOpenConfirmDialog && (
						<ConfirmDialog
							className="z-index-1060"
							show={shouldOpenConfirmDialog}
							title="Thông báo"
							message={validateMessage}
							yes="Có"
							close="Không"
							onCloseClick={() => {
								setValidateMessage('');
								setShouldOpenConfirmDialog(false);
							}}
							onYesClick={() => {
								setShouldOpenConfirmDialog(false);
								onSaveForm();
							}}
						/>
					)}
				</>
			</Formik>
		</Modal>
	);
};

export default PageKeThuoc;
